import { portalText, portalResources } from "../i18n/portal.js";
/**
 * Plain words for a failure on WeTalk's side — never the server's own sentence (2026-10-08).
 *
 * WHY. During the Redis half-open incident the sign-in page showed the api's message verbatim:
 * "The shared cache did not answer an attempt count in time. Nothing was decided; try again." The
 * owner's verdict: "totally cryptic". A 5xx message is written for an engineer reading a log; a
 * customer can do one thing with it — wait and try again — and should be told exactly that.
 *
 * WHAT COUNTS. A status of 500 or more whose code is one of the generic server faults below, or is
 * not a code the contract knows (an ingress's HTML 502, an empty body). A 5xx with a SPECIFIC code
 * keeps its sentence, because those sentences were written for the customer and say something they
 * can act on: `agent_runtime_unavailable` ("WeTalk’s voice runtime is restarting…"),
 * `concurrency_entitlement_reached`, `issuer_unset`, `carrier_failed` ("Check the number…").
 *
 * WHERE. {@link withPlainServerFaults} wraps the fetch the generated client is built with
 * (`./api-gateway.ts`), so every one of the console's several hundred `assertOk` sites throws an
 * `ApiError` that already carries {@link SERVER_FAULT_COPY} — same status, same code, same request
 * id — and no screen can show the server's words by accident. The sign-in screen says it in its own
 * words ({@link signInProblemOf}) and shows the request id as a reference for support.
 */
import { translatedApiMessage } from "../i18n/api-message.js";
import { API_ERROR_CODE, ApiError } from "@wetalk/api-client";

/** What every screen shows for a failure on WeTalk's side. */
export const SERVER_FAULT_COPY = () => (portalText("shell", "something_went_wrong_on_our_side_please_try_again_in_a_min_d6a338b4"));

/** What the sign-in screen shows for the same failure — or for a network that did not get through. */
export const SIGN_IN_SERVER_FAULT_COPY =
  () => (portalText("shell", "we_couldn_t_sign_you_in_just_now_something_went_wrong_on_o_5245d5bf"));

/** The generic server-fault codes: an upstream that failed or was slow, or an internal error. */
const SERVER_FAULT_CODE: ReadonlySet<string> = new Set([
  "upstream_failed",
  "upstream_timed_out",
  "internal_error",
  "audit_write_failed",
  "data_integrity_out_of_range",
]);

const KNOWN_CODE: ReadonlySet<string> = new Set(API_ERROR_CODE);

/** Whether a response is a failure on WeTalk's side whose own sentence must not be shown. */
export function isServerFault(status: number, code: string | null): boolean {
  if (status < 500) return false;
  return code === null || SERVER_FAULT_CODE.has(code) || !KNOWN_CODE.has(code);
}

/** An `ApiError` for a server fault (after the wrapper, its message is already the plain copy). */
export function isServerFaultError(raised: unknown): raised is ApiError {
  return raised instanceof ApiError && isServerFault(raised.status, raised.code === "unknown_code" ? null : raised.code);
}

interface EnvelopeFields {
  readonly code: string | null;
  readonly field: string | null;
}

function envelopeOf(text: string): EnvelopeFields {
  try {
    const parsed: unknown = JSON.parse(text);
    const error = (parsed as { error?: { code?: unknown; field?: unknown } } | null)?.error;
    return {
      code: typeof error?.code === "string" ? error.code : null,
      field: typeof error?.field === "string" ? error.field : null,
    };
  } catch {
    return { code: null, field: null };
  }
}

/**
 * Wraps `fetch` so a server-fault response reaches the generated client with
 * {@link SERVER_FAULT_COPY} as its message. The status, the code (when it had one), the field and
 * every header — `X-WeTalk-Request-Id` among them — are kept; only the sentence changes.
 */
export function withPlainServerFaults(fetch: typeof globalThis.fetch): typeof globalThis.fetch {
  return async (input, init) => {
    const response = await fetch(input, init);
    if (response.status < 500) return response;
    const text = await response.text();
    const { code, field } = envelopeOf(text);
    const headers = new Headers(response.headers);
    headers.delete("content-length");
    if (!isServerFault(response.status, code)) {
      return new Response(text, { status: response.status, statusText: response.statusText, headers });
    }
    headers.set("content-type", "application/json");
    const error: Record<string, unknown> = { message: portalResources.en.shell.something_went_wrong_on_our_side_please_try_again_in_a_min_d6a338b4, field };
    if (code !== null && KNOWN_CODE.has(code)) error["code"] = code;
    return new Response(JSON.stringify({ error }), { status: response.status, statusText: response.statusText, headers });
  };
}

/**
 * The sign-in screen's sentence for a failure: its own words for a server fault or a network that
 * did not get through (`fetch` rejects with a `TypeError`), and the error's own message otherwise —
 * a wrong password, an invalid code, a refused address are written for the person and stay as they are.
 */
export function signInProblemOf(raised: Error): string {
  if (isServerFaultError(raised) || raised instanceof TypeError) return SIGN_IN_SERVER_FAULT_COPY();
  return translatedApiMessage(raised.message) ?? portalText("shell", "errors.original", { message: raised.message });
}

/** The request id to quote to support, for a server fault that carried one. */
export function referenceOf(raised: Error): string | null {
  return isServerFaultError(raised) ? raised.requestId : null;
}
